import {
  OrderedExtraBagsEnum,
  SeatMapResponseEnum,
  type ItineraryBaggage,
} from "@b2bportal/air-booking-api";
import type { AirCfarPriceQuoteData } from "@b2bportal/air-cfar-api";
import type {
  AirChfarDiscount,
  AirChfarPriceQuoteData,
} from "@b2bportal/air-chfar-api";
import type { ArbitraryChargeProduct } from "@b2bportal/arbitrary-charge-api";
import type { Fee as NubankFee } from "@b2bportal/nubank-purchase-api";
import {
  Payment,
  Product,
  type ProductOpaqueValue,
  type QuotedProduct,
} from "@b2bportal/purchase-api";
import {
  type CartQuoteBreakdownProducts,
  cartQuoteSelectors,
  FlightPassengerSelectorsV2,
  FlightSelectors,
  getCartQuoteBreakdownProducts,
  getChildState,
  getNestedChildState,
  ParentState,
  PassengerInformationState,
  SeatSelectors,
  VipSupportSelectors,
} from "@hopper-b2b/checkout";
import {
  type ChoosePaymentProperties,
  type ChooseTravelerProperties,
  type Currency,
  type DisruptionOfferQuote,
  type FiatPrice,
  type ILineItemProps,
  type ISummaryLineItem,
  RegionType,
  SummaryLineItemBaseEnum,
  type TripCategory,
} from "@hopper-b2b/types";
import { createSelector } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import { sum } from "lodash-es";
import type { State, StateValue } from "xstate";

import type { NubankSessionInfo } from "../../../api/session/startSession";
import { amountToFiatPrice } from "../../../utils/prices";
import type { FlightMachineContext } from "../types";

type CheckoutState = State<FlightMachineContext>;
type CheckoutStateWithoutValue = Pick<CheckoutState, "context">;
type CheckoutStateWithAndWithoutValue =
  | CheckoutState
  | CheckoutStateWithoutValue;

export const getIsPriceBreakdownLoading = (
  state: CheckoutStateWithAndWithoutValue
) => {
  const tripPricing = state.context.common.tripPricing;
  const quoteBreakdownPricing =
    FlightSelectors.getQuoteBreakdownFlightPricing(state);

  return quoteBreakdownPricing === undefined && tripPricing === undefined;
};

export const getSelectedTrip = (state: CheckoutStateWithAndWithoutValue) =>
  state.context.flightShop.selectedTrip;

export const getPricing = (state: CheckoutStateWithAndWithoutValue) => {
  const tripPricing = state.context.common.tripPricing;
  const quoteTripPricing =
    FlightSelectors.getQuoteBreakdownFlightPricing(state);
  const validPriceQuote =
    (quoteTripPricing?.pricingByPassenger?.length || 0) > 0;
  return validPriceQuote ? quoteTripPricing : tripPricing;
};

export const getShopPricingInfo = (state: CheckoutStateWithAndWithoutValue) =>
  state.context.flightShop.shopPricingInfo;

export const getSeatMapAvailable = (state: CheckoutStateWithAndWithoutValue) =>
  state.context[ParentState.seatSelection].seatMapAvailable ===
    SeatMapResponseEnum.SeatMapAvailable &&
  !state.context.featureFlags.hideSeats;

export const getSelectedTripDetails = (
  state: CheckoutStateWithAndWithoutValue
) => state.context.flightShop.tripDetails;

export const getCurrencyFromPricingInfo = (
  state: CheckoutStateWithAndWithoutValue
) => {
  const shopPricingInfo = getShopPricingInfo(state);
  return {
    currencyCode:
      (shopPricingInfo.fare?.[0]?.pricing.baseAmount.fiat
        .currencyCode as Currency) || "BRL",
    currencySymbol:
      shopPricingInfo.fare?.[0]?.pricing.baseAmount.fiat.currencySymbol,
  };
};

export const getProductTotalPrice = (
  state: CheckoutStateWithAndWithoutValue
) => {
  return state.context.cartQuote.quoteBreakdown?.productTotal;
};

// TODO: fix type below
export const getFlightTotalInPrices = (
  state: CheckoutStateWithAndWithoutValue
) => {
  const tripPricing = getPricing(state);
  const currency = getCurrencyFromPricingInfo(state);
  const shopPricingInfo = getShopPricingInfo(state);
  const selectedSeats = SeatSelectors.getSelectedSeats(state);

  const productTotalPrice = getFlightBalance(state);

  // After cart update, seats are always in the quote, so balance includes them
  if (productTotalPrice) {
    return productTotalPrice;
  }

  // Fallback cases when quote breakdown is not available yet
  const flightTotal = tripPricing?.totalPricing?.total.fiat.value;
  const farePricing = shopPricingInfo?.fare?.[0]?.pricing;
  const seatTotal = selectedSeats.reduce(
    (sum, seat) => sum + parseFloat(seat.price),
    0
  );

  if (flightTotal) {
    return {
      fiat: {
        ...currency,
        value: flightTotal + seatTotal,
      },
    };
  } else if (farePricing) {
    const fiatBaseAmount = farePricing.baseAmount.fiat.value || 0;
    const fiatTaxAmount = farePricing.taxAmount.fiat.value || 0;
    const fiatAdditionalMargin = farePricing.additionalMargin
      ? farePricing.additionalMargin.fiat.value
      : 0;

    const fiatTotal = fiatBaseAmount + fiatTaxAmount + fiatAdditionalMargin;
    return {
      fiat: {
        currencyCode: farePricing.baseAmount.fiat.currencyCode,
        currencySymbol: farePricing.baseAmount.fiat.currencySymbol,
        value: fiatTotal + seatTotal,
      },
    };
  } else {
    return null;
  }
};

export const getFlightBalance = (state: CheckoutStateWithAndWithoutValue) => {
  return state.context.cartQuote.quoteBreakdown?.balance;
};

export const getChfarDiscount = (state: CheckoutStateWithAndWithoutValue) => {
  const payment = state.context.cartQuote.quoteBreakdown?.payments.find(
    (p) => p.payment.type === Payment.AirChfarExerciseDiscount
  );

  const chfarDiscount = payment
    ? (payment.payment.value as AirChfarDiscount)
    : undefined;

  const isValueZero = chfarDiscount?.totalDiscountAmount.fiat.value === 0;
  return isValueZero ? undefined : chfarDiscount?.totalDiscountAmount;
};

type ProductLineItemType = { key: string; price: FiatPrice };

const getProductLineItem = (
  product: ProductOpaqueValue
): ProductLineItemType[] => {
  switch (product.type) {
    case Product.AirCfar:
      return [
        {
          key: product.type,
          price: (product.value as AirCfarPriceQuoteData).totalPremiumAmount
            .fiat,
        },
      ];
    case Product.AirChfar:
      return [
        {
          key: product.type,
          price: (product.value as AirChfarPriceQuoteData).perPaxPremiumAmount
            .fiat,
        },
      ];
    case Product.AirDisruption: {
      const disruptionProducts = product.value.quotes as DisruptionOfferQuote[];
      return disruptionProducts.map(
        (disruptionProduct: DisruptionOfferQuote) => ({
          key: disruptionProduct.productType,
          price: disruptionProduct.pricePerPax.fiat,
        })
      );
    }
    default:
      return [];
  }
};

export const getConvenienceFeeLineItems = (
  state: CheckoutStateWithAndWithoutValue
): ProductLineItemType[] => {
  const quoteBreakdown = cartQuoteSelectors.getQuoteBreakdown(state);
  const cartProduct = quoteBreakdown?.products?.find(
    (p) => p.product.type === Product.Cart
  );
  if (!cartProduct) return [];

  const { additionalNotes } = cartProduct.product
    .value as ArbitraryChargeProduct;
  if (!additionalNotes) return [];

  try {
    const parsed: unknown = JSON.parse(additionalNotes);
    if (typeof parsed !== "object" || parsed === null) return [];
    const { name, total } = parsed as Partial<NubankFee>;
    if (!name || !total) return [];
    return [{ key: name, price: amountToFiatPrice(total) }];
  } catch {
    return [];
  }
};

export const getProductLineItemsPerPaxPricing = (
  state: CheckoutStateWithAndWithoutValue
) => {
  const quoteBreakdown = cartQuoteSelectors.getQuoteBreakdown(state);
  const productLineItemsPerPax: Record<string, FiatPrice> = {};

  if (quoteBreakdown?.products) {
    const allProductLineItems = quoteBreakdown.products
      .map((quotedProduct: QuotedProduct) =>
        getProductLineItem(quotedProduct.product)
      )
      .flat();

    allProductLineItems.forEach((productLineItem: ProductLineItemType) => {
      productLineItemsPerPax[productLineItem.key] = productLineItem.price;
    });
  }

  return productLineItemsPerPax;
};

export const getBaggageFromBreakdown = ({
  context,
}: CheckoutStateWithAndWithoutValue) => {
  const quotedBaggageProduct = context.cartQuote.quoteBreakdown?.products?.find(
    (product) => product.product.type === Product.AirBaggage
  )?.product?.value as ItineraryBaggage;

  if (
    quotedBaggageProduct &&
    quotedBaggageProduct.orderedExtraBags.OrderedExtraBags ===
      OrderedExtraBagsEnum.PerPassengerExtraBags
  ) {
    // only care about per passenger at the moment
    return quotedBaggageProduct.orderedExtraBags.bagsPerPassenger;
  }

  return {};
};

export const getTripPriceLineItems = (
  state: CheckoutStateWithAndWithoutValue
) => {
  const selectedSeats = SeatSelectors.getSelectedSeats(state);

  const bagsFromBreakdown = getBaggageFromBreakdown(state);
  const tripPricing = getPricing(state);
  const shopPricingInfo = getShopPricingInfo(state);
  const productLineItemsPerPax = getProductLineItemsPerPaxPricing(state);

  if (tripPricing) {
    return tripPricing.pricingByPassenger.map((pricing) => {
      // Sum pricing for all seats for each passenger
      const seats = selectedSeats.reduce(
        (sum, seat) =>
          seat.person_id === pricing.person.id
            ? sum + parseFloat(seat.price)
            : sum,
        0
      );

      const extraBags = bagsFromBreakdown[pricing.person.id];

      const baggageProduct = extraBags
        ? {
            additionalBaggageAdded: {
              currencySymbol: pricing.baseWithoutMargin.fiat.currencySymbol,
              currencyCode: pricing.baseWithoutMargin.fiat.currencyCode,
              value: extraBags.checkedBags.reduce(
                (sum, bag) => sum + bag.price.fiat.value,
                0
              ),
            },
          }
        : {};

      const allProducts = {
        ...baggageProduct,
        ...productLineItemsPerPax,
      };

      const lineItems: ILineItemProps = {
        lineTitle: `${pricing.person.givenName} ${pricing.person.surname}`,
        baseAmount:
          pricing.baseWithoutMargin.fiat.value +
          (pricing.additionalMargin?.fiat.value ?? 0),
        taxesAndFees: pricing.taxes.fiat.value,
        seats: undefined,
        products: Object.keys(allProducts).length > 0 ? allProducts : undefined,
      };

      // TODO: update once INF pax are added to the ancillaries
      if (pricing.passengerType !== "INF") {
        if (seats) {
          lineItems.seats = seats;
        }
      }

      return lineItems;
    });
  } else if (shopPricingInfo.fare && shopPricingInfo.fare.length > 0) {
    const baseAmount =
      shopPricingInfo.fare[0].pricing.baseAmount.fiat.value || 0;
    const taxesAndFees =
      shopPricingInfo.fare[0].pricing.taxAmount.fiat.value || 0;
    return [
      {
        lineTitle: "",
        baseAmount,
        taxesAndFees,
      },
    ];
  }
  return [];
};

const selectQuoteBreakdownSummaryLineItems = createSelector(
  getCartQuoteBreakdownProducts,
  (cartQuoteBreakdownProducts: CartQuoteBreakdownProducts) =>
    Object.entries(cartQuoteBreakdownProducts).flatMap(
      ([productType, data]) => {
        // CFAR products are shown in pricing items section via getProductLineItem
        if (
          productType === Product.AirCfar ||
          productType === Product.AirChfar
        ) {
          return [];
        }

        const summaryLineItem: ISummaryLineItem = {
          type: SummaryLineItemBaseEnum.PRODUCT,
          fiatPrice: data.totalPremiumAmount.fiat,
          // TODO: remove the cast after TS5 migration
          product: productType as Product,
        };

        return [summaryLineItem];
      }
    )
);

export const getSummaryLineItems = (
  state: CheckoutStateWithAndWithoutValue
) => {
  const flightTotalInPrices = getFlightTotalInPrices(state);
  const vipSupportPricing = VipSupportSelectors.getVipSupportPricing(state);
  const quoteBreakdownSummaryLineItems =
    selectQuoteBreakdownSummaryLineItems(state);

  const lineItems: ISummaryLineItem[] = [...quoteBreakdownSummaryLineItems];

  if (vipSupportPricing) {
    lineItems.push({
      type: SummaryLineItemBaseEnum.PRODUCT,
      fiatPrice: vipSupportPricing,
      product: Product.VipSupport,
    });
  }
  if (flightTotalInPrices) {
    lineItems.push({
      type: SummaryLineItemBaseEnum.TOTAL,
      fiatPrice: flightTotalInPrices.fiat,
    });
  }
  return lineItems;
};

export const getFlightSearchDepartureLabel = (
  state: CheckoutStateWithAndWithoutValue
) => state.context.flightShop.departureLabel;

export const getFlightSearchReturnLabel = (
  state: CheckoutStateWithAndWithoutValue
) => state.context.flightShop.returnLabel;

export const getTripCategory = (state: CheckoutStateWithAndWithoutValue) =>
  state.context.flightSearch.tripCategory;

export const getSessionInfo = ({ context }: CheckoutStateWithAndWithoutValue) =>
  context.sessionInfo as unknown as NubankSessionInfo;

export const getStateValue = ({ value }: { value: StateValue }) => value;

export const getShowInfantSeatNotSupportedWarning = (state: CheckoutState) => {
  const childState = getChildState(state.value);
  const nestedChildState = getNestedChildState(state.value);

  return (
    childState === PassengerInformationState.passengerPicker &&
    nestedChildState === "infantSeatNotSupportedWarning"
  );
};

export const getClientAssets = ({
  context,
}: CheckoutStateWithAndWithoutValue) => context.clientAssets;

export const getFlightSearchDestination = ({
  context,
}: CheckoutStateWithAndWithoutValue) => context.flightSearch.destination;

export const getFlightSearchOrigin = ({
  context,
}: CheckoutStateWithAndWithoutValue) => context.flightSearch.origin;

export const getDestinationCity = ({ context }: CheckoutStateWithoutValue) =>
  context.flightShop.departureLabel;

export const getOriginCity = ({ context }: CheckoutStateWithoutValue) =>
  context.flightShop.returnLabel;

export const getFlightSearchDepartureDate = ({
  context,
}: CheckoutStateWithAndWithoutValue) => context.flightSearch.departureDate;

export const getFlightSearchReturnDate = ({
  context,
}: CheckoutStateWithAndWithoutValue) => context.flightSearch.returnDate;

export const getOriginCountryCode = ({
  context,
}: CheckoutStateWithAndWithoutValue) => context.flightSearch.originCountryCode;

export const getDestinationCountryCode = ({
  context,
}: CheckoutStateWithAndWithoutValue) =>
  context.flightSearch.destinationCountryCode;

export const getTripDetails = ({ context }: CheckoutStateWithAndWithoutValue) =>
  context.flightShop.tripDetails;

export const getChooseTravelerProperties = ({
  context,
}: CheckoutStateWithAndWithoutValue): ChooseTravelerProperties => {
  const selectedTripDetails = getTripDetails({ context });

  const tripCategory = getTripCategory({ context }) as TripCategory;
  const destination = getFlightSearchDestination({ context });
  const origin = getFlightSearchOrigin({ context });
  const originCountryCode = getOriginCountryCode({ context });
  const destinationCountryCode = getDestinationCountryCode({ context });
  const departureDate = getFlightSearchDepartureDate({ context });
  const returnDate = getFlightSearchReturnDate({ context });
  const formattedDepartureDate = dayjs(departureDate).format("YYYY-MM-DD");
  const formattedReturnDate =
    returnDate && dayjs(returnDate).format("YYYY-MM-DD");

  return {
    departure_date: formattedDepartureDate,
    destination_country_code: destinationCountryCode,
    destination: destination
      ? `${destination?.id.code.regionType}/${destination?.id.code.code}`
      : `${RegionType.Airport}/${selectedTripDetails.slices[0].destinationCode}`,
    origin_country_code: originCountryCode,
    origin: origin
      ? `${origin?.id.code.regionType}/${origin?.id.code.code}`
      : `${RegionType.Airport}/${selectedTripDetails.slices[0].originCode}`,
    return_date: formattedReturnDate ? formattedReturnDate : undefined,
    trip_type: tripCategory,
  };
};

export const getBagTrackingPropertiesFromBreakdown = ({
  context,
}: CheckoutStateWithAndWithoutValue) => {
  const baggageFromBreakdown = getBaggageFromBreakdown({ context });
  const tripPricing = getPricing({ context });
  let totalCost = 0;
  let totalCheckedBagCount = 0;
  Object.keys(baggageFromBreakdown).forEach((passenger) => {
    // Nubank only supports checked bags, cost is based off that
    totalCost += Object.values(
      baggageFromBreakdown[passenger].checkedBags
    ).reduce((sum, bag) => sum + bag.price.fiat.value, 0);
    totalCheckedBagCount += baggageFromBreakdown[passenger]?.checkedBags.length;
  });

  return {
    extra_bags_price_total: totalCost,
    extra_bags_price_total_currency:
      tripPricing?.totalPricing.total.fiat.currencyCode || "BRL",
    extra_bags_total_travelers_with_checked_bag:
      Object.keys(baggageFromBreakdown).length,
    extra_bags_total_added_checked_bag: totalCheckedBagCount,
    // carry on is not supported (yet)
    extra_bags_total_travelers_with_carry_on: 0,
    extra_bags_total_added_carry_on: 0,
  };
};

export const getChoosePaymentProperties = ({
  context,
}: CheckoutStateWithAndWithoutValue): ChoosePaymentProperties => {
  const chooseTravelerProperties = getChooseTravelerProperties({ context });
  const tripPricing = getPricing({ context });
  const quotedFlightTrackingProperties =
    FlightSelectors.getQuotedFlightTrackingProperties({ context });
  const cartQuoteTrackingProperties =
    cartQuoteSelectors.getCartQuoteTrackingProperties({
      context,
    });
  const totalSelectedPassengers =
    FlightPassengerSelectorsV2.getAllSelectedPassengerIdsParent({
      context,
    }).length;
  const selectedInfants =
    FlightPassengerSelectorsV2.getSelectedLapInfantIdsParent({
      context,
    }).length;
  const userPassengers = FlightPassengerSelectorsV2.getUserPassengersParent({
    context,
  });
  const seatedPassengerIds =
    FlightPassengerSelectorsV2.getSelectedPassengerIdsParent({
      context,
    });
  const selectedChildren = userPassengers.filter((pax) => {
    if (!seatedPassengerIds.includes(pax.id)) return false;
    const age = dayjs().diff(dayjs(pax.dateOfBirth, "YYYY-MM-DD"), "year");
    return age > 1 && age < 2; //TODO: centralize age related logic
  }).length;
  const totalStops = sum(
    getSelectedTripDetails({ context }).slices.map((s) => s.stops)
  );
  const bagProperties = getBagTrackingPropertiesFromBreakdown({ context });

  return {
    ...chooseTravelerProperties,
    ...quotedFlightTrackingProperties,
    ...cartQuoteTrackingProperties,
    ...bagProperties,

    charged_PQ_currency:
      tripPricing?.totalPricing.total.fiat.currencyCode || "BRL",
    total_price: tripPricing?.totalPricing.total.fiat.value || 0,
    pax_total: totalSelectedPassengers,
    selected_pax_children: selectedChildren,
    selected_pax_lap_infants: selectedInfants,
    selected_pax_total: totalSelectedPassengers,
    total_stops: totalStops,
  };
};
